import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AxiosError, AxiosResponse } from 'axios';

import { axios } from '#api/axios';
import { useToast } from '#providers/ToastProvider';
import { PrevHeaderBarWithTitleTemplate } from '#templates';

import GymAddAddress from 'src/refactor_components/gym-add/address';
import GymAddAmenities from 'src/refactor_components/gym-add/amenities';
import GymAddBasic from 'src/refactor_components/gym-add/basic';

type MethodsType = {
  address: string;
  detail: string;
  name: string;
  phone: string;
  introduction: string;
  showerRoom: boolean;
  fittingRoom: boolean;
  sportswear: boolean;
  locker: boolean;
  tower: boolean;
  parkingSpace: boolean;
  images: Array<{ id: string }>;
  lat: string;
  long: string;
};

interface GymRegisterType extends Omit<MethodsType, 'images'> {
  images: string[];
}

const GymAdd = () => {
  const methods = useForm<MethodsType>({
    defaultValues: {
      address: '',
      detail: '',
      name: '',
      phone: '',
      introduction: '',
      showerRoom: false,
      fittingRoom: false,
      sportswear: false,
      locker: false,
      tower: false,
      parkingSpace: false,
      images: []
    }
  });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { mutate: gymAddMutate } = useMutation({
    mutationFn: (payload: string) => axios.post(`gyms/${payload}/v2`),
    onSuccess: () => {
      showToast({
        message: '수업 공간을 추가했습니다.'
      });
      queryClient.invalidateQueries({ queryKey: ['get-coach-gym-list'] });
      queryClient.invalidateQueries({ queryKey: ['gym-list'] });
      return navigate('/profile/gym');
    },
    onError: (error: AxiosError<{ message: string }>) =>
      showToast({
        type: 'fail',
        message: error?.response?.data.message
          ? error?.response?.data.message
          : '일시적인 에러입니다. 다시 시도해주세요.'
      })
  });

  const { mutate } = useMutation({
    mutationFn: async (payload: GymRegisterType) =>
      await axios.post('/gyms', payload),
    onSuccess: (response: AxiosResponse<{ id: string }>) => {
      showToast({
        message: '수업 장소를 등록하였습니다.'
      });
      return gymAddMutate(response.data.id);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      showToast({
        type: 'fail',
        message: error?.response?.data.message
          ? error?.response?.data.message
          : '일시적인 에러입니다. 다시 시도해주세요.'
      });
    }
  });

  const onSubmitGym = (data: MethodsType) => {
    const { images, ...rest } = data;
    const imagesId = images.map(({ id }) => {
      return id;
    });
    mutate({ images: imagesId, ...rest });
  };
  const onError = () => {
    showToast({
      type: 'fail',
      message: '주소를 포함한 필수값을 입력해주세요.'
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmitGym, onError)}>
        <PrevHeaderBarWithTitleTemplate
          prevRoute="../"
          title="수업 장소 등록"
          defaultBackground
          rightContents={<button>등록</button>}
        >
          <div className="p-20 flex flex-col gap-40">
            <GymAddAddress />

            <GymAddBasic />

            <GymAddAmenities />
          </div>
        </PrevHeaderBarWithTitleTemplate>
      </form>
    </FormProvider>
  );
};

export default GymAdd;
